import { fmtDate } from "utils/date";
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import { NoticiaService } from "service/NoticiaService";
import ModalNoticia from "components/Modals/ModalNoticia";
import FSelect from "components/Select/FSelect";
import CustomPagination from "components/Pagination/Pagination";

const noticiaService = new NoticiaService();

function Noticia() {
  const notificationAlertRef = useRef(null);
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [listNoticias, setListNoticias] = useState([]);
  const [listFiltered, setListFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEstado, setFilterEstado] = useState("Todos");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNoticia, setSelectedNoticia] = useState(null);

  // Loading state
  const [loadingBuscar, setLoadingBuscar] = useState(false);

  // Confirm modal states
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ mensaje: "", onConfirm: null });

  const getNoticias = async () => {
    setLoadingBuscar(true);
    try {
      const data = await noticiaService.getNoticiasAll();
      const list = Array.isArray(data) ? data : data?.data || [];
      setListNoticias(list);
      applyFilters(list, searchQuery, filterEstado);
    } catch (error) {
      console.error("Error cargando noticias:", error);
      notify("danger", "Error al cargar las noticias");
    } finally {
      setLoadingBuscar(false);
    }
  };

  const applyFilters = (data, query, estado) => {
    let filtered = data;
    if (estado !== "Todos") {
      filtered = filtered.filter((item) => item.estado === estado);
    }
    if (query) {
      const q = query.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.denominacion || "").toLowerCase().includes(q) ||
        (item.descripcion || "").toLowerCase().includes(q)
      );
    }
    setListFiltered(filtered);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(listNoticias, query, filterEstado);
    setCurrentPage(1);
  };

  const handleFilterEstado = (e) => {
    const estado = e.target.value;
    setFilterEstado(estado);
    applyFilters(listNoticias, searchQuery, estado);
    setCurrentPage(1);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) setSelectedNoticia(null);
  };

  const handleEdit = (noticia) => {
    setSelectedNoticia(noticia);
    setModalOpen(true);
  };

  const handleDelete = async (noticia) => {
    setConfirmData({
      mensaje: `¿Estás seguro de eliminar la noticia "${noticia.denominacion}"? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        setConfirmModal(false);
        try {
          const res = await noticiaService.deleteNoticia(noticia._id);
          if (res?.confirmation === "success") {
            getNoticias();
          } else {
            notify("danger", res?.message || "Error al eliminar");
          }
        } catch (error) {
          console.error("Error eliminando noticia:", error);
          notify("danger", "Error al eliminar la noticia");
        }
      },
    });
    setConfirmModal(true);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = listFiltered.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Noticias / Anuncios</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button
                      color="link"
                      className="btn-icon btn-round"
                      title="Configuración"
                    >
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Zona 2: Barra de filtros */}
                <Row className="align-items-end mb-4">
                  <Col md="8">
                    <FormGroup className="mb-0">
                      <Label for="search">Buscar</Label>
                      <Input
                        type="text"
                        id="search"
                        placeholder="Buscar por título o descripción..."
                        value={searchQuery}
                        onChange={handleSearch}
                        style={{ height: "40px" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup className="mb-0">
                      <Label for="estado">Estado</Label>
                      <FSelect
                        id="estado"
                        value={filterEstado}
                        onChange={handleFilterEstado}
                        options={[
                          { value: "Todos", label: "Todos" },
                          { value: "Activo", label: "Activo" },
                          { value: "Inactivo", label: "Inactivo" },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-0">
                      <Button
                        color="info"
                        block
                        onClick={getNoticias}
                        disabled={loadingBuscar}
                        style={{ height: "40px", padding: "0" }}
                        title="Buscar"
                      >
                        {loadingBuscar ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="tim-icons icon-zoom-split" />
                        )}
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Zona 3: Barra de acciones */}
                <Row className="mb-4">
                  <Col md="6">
                    {/* Espacio reservado para acciones masivas */}
                  </Col>
                  <Col md="6" className="text-right">
                    <Button
                      color="primary"
                      onClick={toggleModal}
                      className="btn-fill"
                    >
                      <i className="tim-icons icon-simple-add mr-2" />
                      Nueva Noticia
                    </Button>
                  </Col>
                </Row>

                <hr className="my-4" />
                <Table>
                  <thead className="text-primary">
                    <tr>
                      <th>Título</th>
                      <th>Descripción</th>
                      <th>Estado</th>
                      <th>Fecha</th>
                      <th className="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((row, index) => (
                      <tr key={row._id || index}>
                        <td>{row.denominacion}</td>
                        <td>{row.descripcion}</td>
                        <td>
                          {row.estado === "Activo" ? (
                            <span className="text-success">Activo</span>
                          ) : (
                            <span className="text-danger">Inactivo</span>
                          )}
                        </td>
                        <td>
                          {row.fechacreacion
                            ? fmtDate(row.fechacreacion)
                            : "-"}
                        </td>
                        <td className="text-right">
                          <Button
                            color="info"
                            size="sm"
                            className="btn-icon btn-link"
                            title="Editar"
                            onClick={() => handleEdit(row)}
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-icon btn-link"
                            title="Eliminar"
                            onClick={() => handleDelete(row)}
                          >
                            <i className="tim-icons icon-trash-simple" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {listFiltered.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No se encontraron registros
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <CustomPagination
                  itemsPerPage={itemsPerPage}
                  totalItems={listFiltered.length}
                  paginate={setCurrentPage}
                  currentPage={currentPage}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <ModalNoticia
        isOpen={modalOpen}
        toggle={toggleModal}
        selectedNoticia={selectedNoticia}
        onSave={getNoticias}
      />

      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)} size="sm" centered>
        <ModalHeader toggle={() => setConfirmModal(false)}>Confirmar acción</ModalHeader>
        <ModalBody><p>{confirmData.mensaje}</p></ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancelar</Button>
          <Button color="danger" onClick={confirmData.onConfirm}>Confirmar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default Noticia;
